/**
 * m3 echo Frontend - Entry Point
 *
 * Initialisiert die React-Anwendung und rendert sie in das DOM.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

// Theme matching OfferAI color scheme
const theme = createTheme({
  palette: {
    primary: {
      main: '#678BA3', // Muted blue-grey
      light: '#8AA4B8',
      dark: '#4D6B80',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5A6C7D', // Slate blue-gray
    },
    success: {
      main: '#4caf50', // Green - Mention found
    },
    warning: {
      main: '#ff9800', // Orange - Low position
    },
    error: {
      main: '#f44336', // Red - Not mentioned
    },
    background: {
      default: '#F5F6F8', // Light gray
      paper: '#ffffff',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#5A6C7D',
    },
  },
  motion: {
    duration: {
      fast: 120,
      base: 180,
      slow: 300,
      enter: 225,
      exit: 195,
    },
    easing: {
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      decelerate: 'cubic-bezier(0.0, 0, 0.2, 1)',
      accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    },
  },
  typography: {
    fontFamily: '"Open Sans", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    h2: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    h3: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    h4: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    h5: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    h6: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
    button: {
      fontWeight: 600,
      fontFamily: '"Open Sans", sans-serif',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'box-shadow 180ms cubic-bezier(0.4,0,0.2,1), transform 180ms cubic-bezier(0.4,0,0.2,1)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#678BA3',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#4D6B80',
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #E5E7EB',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
